import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  deleteTokens,
  getAccessToken,
  getAdminMode,
  getRefreshTokenId,
  saveAccessToken,
  saveRefreshToken,
  saveRefreshTokenId,
  setAdminMode,
} from "../../helpers/tokenManager";
import { HTTPError } from "ky";
import { publicCustomerUserClient } from "../../api";
import { AuthContext, AuthContextType } from "./useAuth";
import LoadingIndicator from "../loading/LoadingIndicator";
import customerUserClient from "../../api/private/customerUserClient";
import { useQueryClient } from "@tanstack/react-query";
import { SUPPORTED_LANGUAGES_TYPE } from "../../i18n";
import {
  getLanguageCode,
  saveLanguageCode,
  useCustomTranslation,
} from "../../helpers/translateHelpers";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { showToast } from "../../helpers/toastHelpers";
import { handleApiError } from "../../helpers/errorHandling";
import {
  AuthCustomerUser,
  CPContactPerson,
  CustomerUserLoginRequest,
  CustomerUserLoginResult,
} from "../../api/models/unified";
import {
  clearUserFromSentry,
  setLanguageToSentry,
  setUserToSentry,
} from "../../helpers/sentryHelpers";

const AuthProvider: React.FC<PropsWithChildren> = (
  props,
): React.ReactElement => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const { t, tError } = useCustomTranslation();

  const [otherAccounts, setOtherAccounts] = useState<CPContactPerson[]>([]);
  const [user, setUser] = useState<AuthCustomerUser>();
  const [authenticated, setAuthenticated] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true);
  const [language, setLanguage] = useState<
    SUPPORTED_LANGUAGES_TYPE | undefined
  >(getLanguageCode() ?? "cs");
  const [customerIsChanging, setCustomerIsChanging] = useState(false);
  const [isAdminMode, setIsAdminModeState] = useState<boolean>(getAdminMode());

  const saveDataToProvider = (result: CustomerUserLoginResult) => {
    if (result.success && result.customerUser) {
      saveRefreshToken(result.refreshToken);
      saveRefreshTokenId(result.refreshTokenId);
      saveAccessToken(result.accessToken);
      setAuthenticated(true);
      setUser(result.customerUser);
      setOtherAccounts(result.contactPeople);
      setUserToSentry(result.customerUser);
    }
  };

  const handleLogin = useCallback(
    async (values: CustomerUserLoginRequest) => {
      // Invalidate all queries when logout
      await queryClient.invalidateQueries();
      return publicCustomerUserClient
        .login(values)
        .then((result) => saveDataToProvider(result));
    },
    [queryClient],
  );

  const handleChangeCustomer = useCallback(
    async (customerId: string) => {
      setCustomerIsChanging(true);
      try {
        const result = await customerUserClient.changeCustomer(customerId);

        saveDataToProvider(result);
        // Clear old data
        await queryClient.invalidateQueries();
      } catch {
        showToast(tError("customer_portal_resign_failed"));
      } finally {
        setCustomerIsChanging(false);
      }
    },
    [queryClient, tError],
  );

  const handleAdminLogin = useCallback(
    async (loginResult: CustomerUserLoginResult) => {
      // Invalidate all queries when admin login
      await queryClient.invalidateQueries();
      saveDataToProvider(loginResult);
      setAdminMode(true);
      setIsAdminModeState(true);
    },
    [queryClient],
  );

  const handleSetAdminMode = useCallback((adminMode: boolean) => {
    setAdminMode(adminMode);
    setIsAdminModeState(adminMode);
  }, []);

  const logout = useCallback(async () => {
    try {
      const refreshTokenId = getRefreshTokenId();

      if (refreshTokenId) {
        await customerUserClient.logout(refreshTokenId);
        deleteTokens();
        clearUserFromSentry();
        setAuthenticated(false);
        setOtherAccounts([]);
        setUser(undefined);
        setIsAdminModeState(false);
        showToast(t("logout_success"), "success");
      }
    } catch (e) {
      handleApiError(e as HTTPError);
    }
  }, [t]);

  const initToken = useCallback(async () => {
    try {
      const token = getAccessToken();

      if (token) {
        const result = await customerUserClient.getLoggedUser();

        if (result.customerUser) {
          setAuthenticated(true);
          setUser(result.customerUser);
          setOtherAccounts(result.contactPeople);
        }
      }
    } catch {
      await logout();
    } finally {
      setLoadingInitial(false);
    }
  }, [logout]);

  const onLanguageChange = useCallback(
    (lng: SUPPORTED_LANGUAGES_TYPE) => {
      saveLanguageCode(lng);
      dayjs.locale(lng);
      setLanguage(lng);
      setLanguageToSentry(lng);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      i18n.changeLanguage(lng);
    },
    [i18n],
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    initToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const memoedValue = useMemo<AuthContextType>(
    () => ({
      user,
      authenticated,
      loading: loadingInitial,
      onLogin: handleLogin,
      onLogout: logout,
      setUser,
      setAuthenticated,
      language,
      setLanguage: onLanguageChange,
      setOtherAccounts,
      otherAccounts,
      onChangeCustomer: handleChangeCustomer,
      onAdminLogin: handleAdminLogin,
      customerIsChanging,
      isAdminMode,
      setIsAdminMode: handleSetAdminMode,
    }),
    [
      authenticated,
      loadingInitial,
      handleLogin,
      logout,
      user,
      onLanguageChange,
      language,
      otherAccounts,
      setOtherAccounts,
      handleChangeCustomer,
      handleAdminLogin,
      customerIsChanging,
      isAdminMode,
      handleSetAdminMode,
    ],
  );

  return (
    <AuthContext.Provider value={memoedValue}>
      {loadingInitial ? <LoadingIndicator /> : props.children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
